// src/pages/couriers/CourierAssignmentRulesPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Settings, Plus, Trash2, Edit, X, Search, AlertCircle, RefreshCw,
  Copy, Star
} from 'lucide-react';
import { 
  getAllAssignmentRules, createAssignmentRule, updateAssignmentRule,
  deleteAssignmentRule, toggleAssignmentRule, setDefaultRule, unsetDefaultRule
} from '../../services/api/courierAssignmentRule';
import { getBusinessCouriers } from '../../services/api/businessCourier';
import { getCities, getLocationHierarchy } from '../../services/api/orderReference';
import { getShippingZones } from '../../services/api/shipping';
import { getShops } from '../../services/api/shops';
import { usePermissions } from '../../hooks/usePermissions';
import { useAlert } from '../../contexts/AlertContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import RuleCard from './components/RuleCard';
import RuleModal from './components/RuleModal';
import DeleteRuleModal from './components/DeleteRuleModal';
import styles from './CourierAssignmentRulesPage.module.css';

const CourierAssignmentRulesPage = () => {
  const { t } = useLocalization();
  const { hasPermission } = usePermissions();
  const { success, error: showError } = useAlert();

  const [rules, setRules] = useState([]);
  const [businessCouriers, setBusinessCouriers] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [shippingZones, setShippingZones] = useState([]);
  const [shops, setShops] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourier, setFilterCourier] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('courier');
  const [ruleForm, setRuleForm] = useState({
    businessCourierId: '',
    name: '',
    description: '',
    isActive: true,
    isDefault: false,
    rules: {
      cityRules: [],
      districtRules: [],
      provinceRules: [],
      shippingZoneRules: [],
      shopRules: []
    }
  });
  const [expandedRules, setExpandedRules] = useState(new Set());

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setIsLoading(true);
      setLoadingError(null);
      const [rulesData, couriersData, citiesData, locationData, zonesData, shopsData] = await Promise.all([
        getAllAssignmentRules(),
        getBusinessCouriers(),
        getCities(),
        getLocationHierarchy(),
        getShippingZones(),
        getShops()
      ]);
      setRules(rulesData.rules || []);
      setBusinessCouriers(couriersData.couriers || []);
      setCities(citiesData.cities || []);
      setShippingZones(Array.isArray(zonesData) ? zonesData : (zonesData.shippingZones || []));
      setShops(Array.isArray(shopsData) ? shopsData : (shopsData.shops || []));
      if (locationData.provinces) {
        const provincesArray = locationData.provinces || [];
        const districtsArray = [];
        provincesArray.forEach(province => {
          if (province.Districts) {
            districtsArray.push(...province.Districts);
          }
        });
        setProvinces(provincesArray);
        setDistricts(districtsArray);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setLoadingError(err.response?.data?.message || t('couriers.ruleLoadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenRuleModal = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setRuleForm({
        businessCourierId: rule.businessCourierId,
        name: rule.name,
        description: rule.description || '',
        isActive: rule.isActive,
        isDefault: rule.isDefault,
        rules: rule.rules || {
          cityRules: [],
          districtRules: [],
          provinceRules: [],
          shippingZoneRules: [],
          shopRules: []
        }
      });
    } else {
      setEditingRule(null);
      setRuleForm({
        businessCourierId: '',
        name: '',
        description: '',
        isActive: true,
        isDefault: false,
        rules: {
          cityRules: [],
          districtRules: [],
          provinceRules: [],
          shippingZoneRules: [],
          shopRules: []
        }
      });
    }
    setShowRuleModal(true);
  };

  const handleCloseRuleModal = () => {
    setShowRuleModal(false);
    setEditingRule(null);
  };

  const handleSaveRule = async () => {
    try {
      setIsSubmitting(true);
      if (editingRule) {
        await updateAssignmentRule(editingRule.id, ruleForm);
        success(t('couriers.ruleUpdatedSuccess'));
      } else {
        await createAssignmentRule(ruleForm);
        success(t('couriers.ruleCreatedSuccess'));
      }
      handleCloseRuleModal();
      await loadAllData();
    } catch (err) {
      console.error('Failed to save rule:', err);
      showError(err.response?.data?.message || (editingRule ? t('couriers.ruleUpdateError') : t('couriers.ruleCreateError')));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleRule = async (ruleId, currentStatus) => {
    try {
      await toggleAssignmentRule(ruleId);
      success(!currentStatus ? t('couriers.ruleActivatedSuccess') : t('couriers.ruleDeactivatedSuccess'));
      await loadAllData();
    } catch (err) {
      console.error('Failed to toggle rule:', err);
      showError(err.response?.data?.message || t('couriers.ruleToggleError'));
    }
  };

  const handleToggleDefault = async (rule) => {
    try {
      if (rule.isDefault) {
        await unsetDefaultRule(rule.id);
        success(t('couriers.defaultUnsetSuccess'));
      } else {
        await setDefaultRule(rule.id);
        success(t('couriers.defaultSetSuccess'));
      }
      await loadAllData();
    } catch (err) {
      console.error('Failed to toggle default:', err);
      showError(err.response?.data?.message || t('couriers.defaultToggleError'));
    }
  };

  const handleOpenDeleteModal = (rule) => {
    setRuleToDelete(rule);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setRuleToDelete(null);
  };

  const handleDeleteRule = async () => {
    if (!ruleToDelete) return;
    try {
      setIsSubmitting(true);
      await deleteAssignmentRule(ruleToDelete.id);
      success(t('couriers.ruleDeletedSuccess'));
      handleCloseDeleteModal();
      await loadAllData();
    } catch (err) {
      console.error('Failed to delete rule:', err);
      showError(err.response?.data?.message || t('couriers.ruleDeleteError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicateRule = (rule) => {
    setEditingRule(null);
    setRuleForm({
      businessCourierId: rule.businessCourierId,
      name: `${rule.name} (${t('couriers.copy')})`,
      description: rule.description || '',
      isActive: false,
      isDefault: false,
      rules: rule.rules || {
        cityRules: [],
        districtRules: [],
        provinceRules: [],
        shippingZoneRules: [],
        shopRules: []
      }
    });
    setShowRuleModal(true);
  };

  const toggleRuleExpansion = (ruleId) => {
    setExpandedRules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ruleId)) {
        newSet.delete(ruleId);
      } else {
        newSet.add(ruleId);
      }
      return newSet;
    });
  };

  const filteredAndSortedRules = rules
    .filter(rule => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          rule.name?.toLowerCase().includes(searchLower) ||
          rule.description?.toLowerCase().includes(searchLower) ||
          rule.CourierCompany?.name?.toLowerCase().includes(searchLower)
        );
      }
      return true;
    })
    .filter(rule => {
      if (filterCourier !== 'all') {
        return rule.businessCourierId === parseInt(filterCourier);
      }
      return true;
    })
    .filter(rule => {
      if (filterStatus === 'active') return rule.isActive;
      if (filterStatus === 'inactive') return !rule.isActive;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'courier':
          return (a.CourierCompany?.name || '').localeCompare(b.CourierCompany?.name || '');
        case 'default':
          return (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0);
        case 'status':
          return (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0);
        case 'date':
          return new Date(b.createdAt) - new Date(a.createdAt);
        default:
          return 0;
      }
    });

  const rulesByCourier = filteredAndSortedRules.reduce((acc, rule) => {
    const courierId = rule.businessCourierId;
    if (!acc[courierId]) {
      acc[courierId] = {
        courier: rule.CourierCompany,
        rules: []
      };
    }
    acc[courierId].rules.push(rule);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className={styles.assignmentRulesPage}>
        <PageHeader
          title={t('couriers.assignmentRules')}
          description={t('couriers.assignmentRulesDescription')}
          icon={Settings}
        />

        <div className={styles.loadingState}>
          <div className={styles.loadingSpinnerLarge}></div>
          <h3>{t('couriers.loadingRules')}</h3>
          <p>{t('couriers.loadingRulesMessage')}</p>
        </div>
      </div>
    );
  }

  if (loadingError) {
    return (
      <div className={styles.assignmentRulesPage}>
        <PageHeader
          title={t('couriers.assignmentRules')}
          description={t('couriers.assignmentRulesDescription')}
          icon={Settings}
        />

        <div className={styles.errorState}>
          <AlertCircle className={styles.errorIcon} size={48} />
          <h3>{t('couriers.unableToLoadRules')}</h3>
          <p>{loadingError}</p>
          <button onClick={loadAllData} className={styles.retryButton}>
            <RefreshCw size={16} />
            {t('couriers.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.assignmentRulesPage}>
      <PageHeader
        title={t('couriers.assignmentRules')}
        description={t('couriers.assignmentRulesDescription')}
        icon={Settings}
        actions={
          hasPermission('manage_courier_assignment_rules') && (
            <button onClick={() => handleOpenRuleModal()} className={styles.primaryButton}>
              <Plus size={20} />
              {t('couriers.createRule')}
            </button>
          )
        }
      />

      <div className={styles.pageControls}>
        <div className={styles.searchInputWrapper}>
          <Search className={styles.searchIcon} size={20} />
          <input
            type="text"
            placeholder={t('couriers.searchRules')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className={styles.clearSearchButton}>
              <X size={16} />
            </button>
          )}
        </div>

        <div className={styles.filterRow}>
          <div className={styles.filterGroup}>
            <label>{t('couriers.courier')}:</label>
            <select value={filterCourier} onChange={(e) => setFilterCourier(e.target.value)}>
              <option value="all">{t('couriers.allCouriers')}</option>
              {businessCouriers.map(bc => (
                <option key={bc.id} value={bc.id}>
                  {bc?.accountName}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.filterGroup}>
            <label>{t('couriers.status')}:</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">{t('couriers.allStatus')}</option>
              <option value="active">{t('couriers.active')}</option>
              <option value="inactive">{t('couriers.inactiveStatus')}</option>
            </select>
          </div>
          <div className={styles.filterGroup}>
            <label>{t('couriers.sortBy')}:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="courier">{t('couriers.sortByCourier')}</option>
              <option value="default">{t('couriers.defaultFirst')}</option>
              <option value="status">{t('couriers.sortByStatus')}</option>
              <option value="date">{t('couriers.sortByDate')}</option>
            </select>
          </div>
        </div>

        <div className={styles.statsBar}>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>{t('couriers.totalRules')}:</span>
            <span className={styles.statValue}>{rules.length}</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>{t('couriers.active')}:</span>
            <span className={styles.statValue}>{rules.filter(r => r.isActive).length}</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>{t('couriers.defaultRules')}:</span>
            <span className={styles.statValue}>{rules.filter(r => r.isDefault).length}</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>{t('couriers.couriersConfigured')}:</span>
            <span className={styles.statValue}>{Object.keys(rulesByCourier).length}</span>
          </div>
        </div>
      </div>

      <div className={styles.pageContent}>
        {filteredAndSortedRules.length === 0 ? (
          <div className={styles.emptyState}>
            {searchTerm || filterCourier !== 'all' || filterStatus !== 'all' ? (
              <>
                <Search className={styles.emptyIcon} size={48} />
                <h3>{t('couriers.noRulesFound')}</h3>
                <p>{t('couriers.noMatchingRules')}</p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterCourier('all');
                    setFilterStatus('all');
                  }}
                  className={styles.secondaryButton}
                >
                  {t('couriers.clearFilters')}
                </button>
              </>
            ) : (
              <>
                <Settings className={styles.emptyIcon} size={48} />
                <h3>{t('couriers.noRulesYet')}</h3>
                <p>{t('couriers.noRulesDescription')}</p>
                {hasPermission('manage_courier_assignment_rules') && (
                  <button onClick={() => handleOpenRuleModal()} className={styles.primaryButton}>
                    <Plus size={20} />
                    {t('couriers.createFirstRule')}
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className={styles.rulesContainer}>
            {sortBy === 'courier' ? (
              Object.entries(rulesByCourier).map(([courierId, { courier, rules: courierRules }]) => (
                <div key={courierId} className={styles.courierGroup}>
                  <div className={styles.courierGroupHeader}>
                    <h2>{courier?.name}</h2>
                    <div className={styles.courierGroupInfo}>
                      <span className={styles.ruleCount}>
                        {courierRules.length} {courierRules.length === 1 ? t('couriers.rule') : t('couriers.rules')}
                      </span>
                      {courierRules.some(r => r.isDefault) && (
                        <span className={styles.hasDefaultBadge}>
                          <Star size={12} />
                          {t('couriers.hasDefault')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={styles.rulesGrid}>
                    {courierRules.map(rule => (
                      <RuleCard
                        key={rule.id}
                        rule={rule}
                        isExpanded={expandedRules.has(rule.id)}
                        onToggleExpansion={toggleRuleExpansion}
                        onToggleRule={handleToggleRule}
                        onToggleDefault={handleToggleDefault}
                        onDuplicate={handleDuplicateRule}
                        onEdit={handleOpenRuleModal}
                        onDelete={handleOpenDeleteModal}
                        showCourierName={sortBy !== 'courier'}
                        cities={cities}
                        districts={districts}
                        provinces={provinces}
                        shippingZones={shippingZones}
                        shops={shops}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.rulesGrid}>
                {filteredAndSortedRules.map(rule => (
                  <RuleCard
                    key={rule.id}
                    rule={rule}
                    isExpanded={expandedRules.has(rule.id)}
                    onToggleExpansion={toggleRuleExpansion}
                    onToggleRule={handleToggleRule}
                    onToggleDefault={handleToggleDefault}
                    onDuplicate={handleDuplicateRule}
                    onEdit={handleOpenRuleModal}
                    onDelete={handleOpenDeleteModal}
                    showCourierName={sortBy !== 'courier'}
                    cities={cities}
                    districts={districts}
                    provinces={provinces}
                    shippingZones={shippingZones}
                    shops={shops}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showRuleModal && (
        <RuleModal
          isOpen={showRuleModal}
          onClose={handleCloseRuleModal}
          editingRule={editingRule}
          ruleForm={ruleForm}
          setRuleForm={setRuleForm}
          businessCouriers={businessCouriers}
          cities={cities}
          districts={districts}
          provinces={provinces}
          shippingZones={shippingZones}
          shops={shops}
          onSave={handleSaveRule}
          isSubmitting={isSubmitting}
        />
      )}

      {showDeleteModal && (
        <DeleteRuleModal
          isOpen={showDeleteModal}
          onClose={handleCloseDeleteModal}
          rule={ruleToDelete}
          onConfirm={handleDeleteRule}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};

export default CourierAssignmentRulesPage;